import type { GetStaticProps } from "next"
import { LoadingIndicatorBox } from "@/components/resources/components/LoadingIndicatorBox"
import { PageMeta } from "@/components/resources/components/Seo/PageMeta"
import {
  getPostPaths,
  getSinglePost,
  type PostProps,
} from "@/components/resources/data/sanity/posts"
import { SiteLayout } from "@/components/resources/layouts/site"
import { ArticlePage } from "@/components/resources/templates/ArticlePage"
import { shouldSkipStaticBuild } from "@/components/resources/utils/build"

const SinglePost = ({ post, meta }: PostProps) => {
  if (!post) {
    return <LoadingIndicatorBox />
  }

  return (
    <>
      <PageMeta meta={meta} />
      <ArticlePage {...post} />
    </>
  )
}

SinglePost.getLayout = SiteLayout

export default SinglePost

export async function getStaticPaths() {
  if (shouldSkipStaticBuild()) {
    return {
      paths: [],
      fallback: true,
    }
  }

  const paths = await getPostPaths("weekly-fundamentals")

  return {
    paths: paths.map(({ slug, category }) => ({ params: { category, slug } })),
    fallback: true, // Allow publishing new posts without build
  }
}

export const getStaticProps: GetStaticProps<PostProps, { slug?: string }> = async ({
  params,
  draftMode,
}) => {
  const { slug } = params ?? {}

  if (!slug) {
    throw new Error("Slug cannot be empty, check getStaticPaths")
  }

  const props = await getSinglePost({
    category: "weekly-fundamentals",
    slug,
    draft: draftMode,
  })
  if (!props) {
    /* NOTE: Risk here is that Sanity API is actually down, in which case this should throw error to keep the page stale. Though there's no effect since ISR is not enabled.
     * To fix/reduce the risk, we should also fetch navigation or something similar from Sanity which always is there and
     * determine based on that if this truly is notFound or API is down.
     */

    return {
      notFound: true,
      props: {},
    }
  }

  return {
    props,
    revalidate: 180, // TODO: temporary to allow content editing without an hour wait for build
  }
}
